import { Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MedrFormData } from 'src/app/core/models/customer';
import { edrServiceOptions, slaOptions, deploymentOptions } from '../../../mock-data/services-mock-data';

@Injectable({
  providedIn: 'root',
})
export class MedrFormService {
  constructor(private fb: FormBuilder) {}

  // Custom validator for boolean fields that must be explicitly set (not null/undefined)
  private booleanRequiredValidator(control: AbstractControl): ValidationErrors | null {
    // For EDR Connect, the user must explicitly confirm their API credentials status
    // The form should be valid whether they set it to true or false
    return control.value !== null && control.value !== undefined ? null : { required: true };
  }

  createMedrForm(data?: MedrFormData): FormGroup<MedrForm> {
    const form = this.fb.group<MedrForm>({
      selectedService: this.fb.control(null, [Validators.required]),
      // EDR Connect specific fields
      apiCredentialsProvided: this.fb.control(false),
      // EDR Complete and Control fields
      totalMachines: this.fb.control(0),
      // EDR Complete specific fields
      networkDiscovery: this.fb.control(false),
    });

    // Initialize with data if provided
    if (data) {
      this.patchFormWithData(form, data);
    }

    return form;
  }

  private patchFormWithData(form: FormGroup<MedrForm>, data: MedrFormData): void {
    form.patchValue(data);

    // Apply conditional validation if service is already selected
    if (data.selectedService) {
      this.applyConditionalValidation(form, data.selectedService);
    }
  }

  selectService(form: FormGroup<MedrForm>, serviceValue: string): void {
    // Clear previous service selection
    form.get('selectedService')?.setValue(serviceValue);

    // Reset field values when service changes
    this.resetServiceSpecificFields(form, serviceValue);

    // Apply conditional validation based on selected service
    this.applyConditionalValidation(form, serviceValue);
  }

  private resetServiceSpecificFields(form: FormGroup<MedrForm>, serviceValue: string): void {
    // Reset all service-specific fields to default values
    const resetValues: any = {
      apiCredentialsProvided: false,
      networkDiscovery: false,
      totalMachines: 0,
    };

    // Don't reset totalMachines if the new service requires it
    if (serviceValue !== 'edr-complete' && serviceValue !== 'edr-control') {
      resetValues.totalMachines = 0;
    }

    form.patchValue(resetValues);
  }

  private applyConditionalValidation(form: FormGroup<MedrForm>, serviceValue: string): void {
    const totalMachinesControl = form.get('totalMachines');
    const apiCredentialsControl = form.get('apiCredentialsProvided');
    const networkDiscoveryControl = form.get('networkDiscovery');

    // Remove all conditional validators first
    totalMachinesControl?.clearValidators();
    apiCredentialsControl?.clearValidators();

    // Apply service-specific validation
    switch (serviceValue) {
      case 'edr-connect':
        // EDR Connect requires API credentials and SOAR integration
        apiCredentialsControl?.setValidators([this.booleanRequiredValidator]);
        break;

      case 'edr-complete':
        // EDR Complete requires total machines count. network discovery is optional
        totalMachinesControl?.setValidators([Validators.required, Validators.min(1)]);
        networkDiscoveryControl?.clearValidators();
        break;

      case 'edr-control':
        // EDR Control requires total machines and endpoint control
        totalMachinesControl?.setValidators([Validators.required, Validators.min(1)]);
        break;
    }

    // Update validity for all affected controls
    totalMachinesControl?.updateValueAndValidity();
    apiCredentialsControl?.updateValueAndValidity();
    networkDiscoveryControl?.updateValueAndValidity();
    // Force form validation update
    form.updateValueAndValidity();
  }

  getFormData(form: FormGroup<MedrForm>): MedrFormData {
    const value = form.value;
    return {
      selectedService: value.selectedService ?? '',
      apiCredentialsProvided: value.apiCredentialsProvided ?? false,
      totalMachines: value.totalMachines ?? 0,
      networkDiscovery: value.networkDiscovery ?? false,
    };
  }

  onTotalMachinesChange(form: FormGroup<MedrForm>, value: number | null): void {
    const numericValue = value || 0;
    form.get('totalMachines')?.setValue(numericValue);
    form.updateValueAndValidity();
  }

  // Helper methods for dropdown value handling
  getDropdownValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }
    return '';
  }

  findDropdownObject(options: any[], value: string): any {
    if (!value) return null;
    return options.find((option) => option.value === value) || null;
  }

  // Static data methods
  getServiceOptions() {
    return edrServiceOptions;
  }

  getSlaOptions() {
    return slaOptions;
  }

  getDeploymentOptions() {
    return deploymentOptions;
  }

  getServiceDescription(serviceValue: string): string {
    const service = this.getServiceOptions().find((s: any) => s.value === serviceValue);
    return service?.description || '';
  }

  // Debug method
  debugFormState(form: FormGroup<MedrForm>): void {
    console.log('=== MEDR Form Debug ===');
    console.log('Form Valid:', form.valid);
    console.log('Form Invalid:', form.invalid);
    console.log('Form Pending:', form.pending);
    console.log('Form Dirty:', form.dirty);
    console.log('Form Touched:', form.touched);
    console.log('Form Errors:', form.errors);

    console.log('Selected Service:', form.get('selectedService')?.value);
    console.log('Selected Service Valid:', form.get('selectedService')?.valid);
    console.log('Selected Service Errors:', form.get('selectedService')?.errors);

    console.log('Total Machines:', form.get('totalMachines')?.value);
    console.log('Total Machines Valid:', form.get('totalMachines')?.valid);
    console.log('Total Machines Errors:', form.get('totalMachines')?.errors);
    console.log('Network Discovery:', form.get('networkDiscovery')?.value);
    console.log('Network Discovery Valid:', form.get('networkDiscovery')?.valid);
    console.log('Network Discovery Errors:', form.get('networkDiscovery')?.errors);

    // Check all form controls
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      console.log(`${key}:`, {
        value: control?.value,
        valid: control?.valid,
        invalid: control?.invalid,
        errors: control?.errors,
        touched: control?.touched,
        dirty: control?.dirty,
      });
    });
  }
}

export interface MedrForm {
  selectedService: FormControl<string | null>;
  apiCredentialsProvided: FormControl<boolean | null>;
  totalMachines: FormControl<number | null>;
  networkDiscovery: FormControl<boolean | null>;
}
